import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCampusDetails } from "../../../redux/campusesSlice";
import ViewReportModal from "./ViewReportModal";
import SurveyReport from "./SurveyReport";
import toast from "react-hot-toast";

const Surveys = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [viewReportOpen, setViewReportOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [viewSectionReport, setViewSectionReport] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  
  const { campusDetails, loading } = useSelector((state) => state.campuses);
  
  const selectedCampus = campusDetails.find(campus => campus.id === selectedCampusId) || { classes: [] };
  const availableClasses = selectedCampus.classes || [];
  
  const selectedClass = availableClasses.find(cls => cls.id === selectedClassId) || { sections: [] };
  const availableSections = selectedClass.sections || [];
  
  const selectedSection = availableSections.find(section => section.id === selectedSectionId) || { students: [] };
  const availableStudents = selectedSection.students || [];
  
  // Dummy surveys data
  const availableSurveys = [
    {
      id: "SURV001",
      title: "Parent Satisfaction Survey 2024",
      dateConducted: "2024-01-15",
      targetAudience: "Parents",
      description: "Comprehensive survey to assess parent satisfaction with school services"
    },
    {
      id: "SURV002", 
      title: "Student Learning Experience Survey",
      dateConducted: "2024-01-20",
      targetAudience: "Students",
      description: "Survey to evaluate student learning experience and academic satisfaction"
    },
    {
      id: "SURV003",
      title: "Teacher Professional Development Survey",
      dateConducted: "2024-01-25",
      targetAudience: "Teachers",
      description: "Assessment of teacher satisfaction with professional development opportunities"
    },
    {
      id: "SURV004",
      title: "School Safety and Security Survey",
      dateConducted: "2024-02-01",
      targetAudience: "All Stakeholders",
      description: "Comprehensive survey on school safety measures and security protocols"
    },
    {
      id: "SURV005",
      title: "Extracurricular Activities Feedback",
      dateConducted: "2024-02-05",
      targetAudience: "Students & Parents",
      description: "Feedback on extracurricular activities and sports programs"
    }
  ];
  
  const selectedSurvey = availableSurveys.find(survey => survey.id === selectedSurveyId);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCampusDetails());
    }
  }, [isOpen, dispatch]);
  
  useEffect(() => {
    if (selectedCampusId === "") {
      setSelectedClassId("");
      setSelectedSectionId("");
      setSelectedStudentId("");
    }
  }, [selectedCampusId]);
  
  useEffect(() => {
    if (selectedClassId === "") {
      setSelectedSectionId("");
      setSelectedStudentId("");
    }
  }, [selectedClassId]);
  
  useEffect(() => {
    if (selectedSectionId === "") {
      setSelectedStudentId("");
      setSelectedSurveyId("");
    }
  }, [selectedSectionId]);
  
  useEffect(() => {
    if (selectedStudentId === "") {
      setSelectedSurveyId("");
    }
  }, [selectedStudentId]);

  const handleViewSurveyReport = () => {
    if (selectedSurveyId) {
      setViewSectionReport(true);
      setViewReportOpen(true);
    } else {
      toast.error("Please select a survey first");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-[60%] relative">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="campus" className="block mb-2">
                Campus
              </label>
              <select 
                id="campus" 
                className="border p-2 rounded w-full"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                disabled={loading}
              >
                <option value="" disabled>
                  Choose a campus
                </option>
                {campusDetails.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="grade" className="block mb-2">
                Grade
              </label>
              <select 
                id="grade" 
                className="border p-2 rounded w-full"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                disabled={!selectedCampusId || loading}
              >
                <option value="" disabled>
                  Choose a grade
                </option>
                {availableClasses.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.className}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="section" className="block mb-2">
                Section
              </label>
              <select 
                id="section" 
                className="border p-2 rounded w-full"
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                disabled={!selectedClassId || loading}
              >
                <option value="" disabled>
                  Choose a section
                </option>
                {availableSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.sectionName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="studentName" className="block mb-2">
                Select a Role
              </label>
              <select 
                id="studentName" 
                className="border p-2 rounded w-full"
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                disabled={!selectedSectionId || loading}
              >
                <option value="" disabled>
                  Choose a Teacher / Parent / Learner / Admin Staff
                </option>
                {availableStudents.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.fullName}
                  </option>
                ))}
              </select>
            </div>
            <div>
                <label htmlFor="SurveyId" className="block mb-2">Select a Survey</label>
                <select 
                    id="SurveyId" 
                    className="border p-2 rounded w-full"
                    value={selectedSurveyId}
                    onChange={(e) => setSelectedSurveyId(e.target.value)}
                    disabled={!selectedStudentId || loading}
                >
                    <option value="" disabled>
                        Choose a Survey
                    </option>
                    {availableSurveys.map((survey) => (
                        <option key={survey.id} value={survey.id}>
                            {survey.title}
                        </option>
                    ))}
                </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="bg-gray-400 px-4 py-2 rounded text-white"
            >
              Close
            </button>
            <button
              onClick={handleViewSurveyReport}
              className="bg-[#241763] px-4 py-2 rounded text-white"
              disabled={!selectedSurveyId || loading}
            >
              View Survey Report
            </button>
          </div>
        </div>
      </div>

      {viewSectionReport ? (
        <SurveyReport
          isOpen={viewReportOpen}
          onClose={() => setViewReportOpen(false)}
          selectedSurvey={selectedSurvey}
          sectionName={selectedSection.sectionName}
          className={selectedClass.className}
          campusName={selectedCampus.name}
          targetAudience={selectedSurvey?.targetAudience}
        />
      ) : (
        <ViewReportModal
          isOpen={viewReportOpen}
          onClose={() => setViewReportOpen(false)}
          studentId={selectedStudentId}
          sectionId={selectedSectionId}
          isSectionReport={viewSectionReport}
          sectionName={selectedSection.sectionName}
          className={selectedClass.className}
          campusName={selectedCampus.name} 
        />
      )}
    </>
  );
};

export default Surveys;